/**
 * Parsing and labels for the typed date and time fields. Values stay in the
 * forms the rest of the app already uses — `YYYY-MM-DD` and 24-hour `HH:MM` —
 * so swapping a field never reshapes the form behind it. Everything here is a
 * calendar date or a wall-clock time: no time zone takes part.
 */
import { format } from 'date-fns';

const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;

const pad = (n: number) => String(n).padStart(2, '0');

/** `YYYY-MM-DD` for a year, 1-based month and day, or null when no such date exists. */
export function isoDate(year: number, month: number, day: number): string | null {
  const d = new Date(Date.UTC(year, month - 1, day));
  const real =
    d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
  return real ? `${year}-${pad(month)}-${pad(day)}` : null;
}

/** Two-digit years read as this century. */
function fullYear(text: string): number {
  const n = Number(text);
  return text.length <= 2 ? 2000 + n : n;
}

function monthFromName(name: string): number | null {
  const index = MONTHS.indexOf(name.slice(0, 3).toLowerCase() as (typeof MONTHS)[number]);
  return index === -1 ? null : index + 1;
}

/**
 * A typed date as `YYYY-MM-DD`, or null when it is not one yet. Accepts
 * `2026-10-22`, `10/22/2026`, `10/22/26`, `10/22`, `Oct 22`, `October 22, 2026`
 * and the field's own label (`Thu, Oct 22, 2026`). A date with no year takes
 * `referenceYear`.
 */
export function parseDateInput(text: string, referenceYear: number): string | null {
  const t = text.trim().replace(/^[a-z]+,\s*/i, '');
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (iso) return isoDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const numeric = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?$/.exec(t);
  if (numeric) {
    const year = numeric[3] ? fullYear(numeric[3]) : referenceYear;
    return isoDate(year, Number(numeric[1]), Number(numeric[2]));
  }
  const named = /^([a-z]+)\.?\s+(\d{1,2})(?:,?\s+(\d{4}))?$/i.exec(t);
  if (named) {
    const month = monthFromName(named[1]);
    const year = named[3] ? Number(named[3]) : referenceYear;
    return month ? isoDate(year, month, Number(named[2])) : null;
  }
  return null;
}

/**
 * Like {@link parseDateInput}, but a date typed without a year that would land
 * before `floor` is read as next year's: in November, "1/14" means the coming
 * January, not the one that has passed. A date with an explicit year is kept.
 */
export function parseDateInputFrom(
  text: string,
  referenceYear: number,
  floor: string | undefined,
): string | null {
  const parsed = parseDateInput(text, referenceYear);
  if (!parsed || !floor || parsed >= floor) return parsed;
  const nextYear = parseDateInput(text, referenceYear + 1);
  return nextYear && nextYear !== parsed ? nextYear : parsed;
}

/** "Thu, Oct 22, 2026" for `2026-10-22`; "" when there is no date. */
export function formatDateLabel(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return '';
  return format(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])), 'EEE, MMM d, yyyy');
}

/** The hour on a 24-hour clock, or null when the hour and suffix disagree. */
function hour24(hour: number, suffix: string | undefined): number | null {
  if (!suffix) return hour <= 23 ? hour : null;
  if (hour < 1 || hour > 12) return null;
  const pm = suffix.startsWith('p');
  if (hour === 12) return pm ? 12 : 0;
  return pm ? hour + 12 : hour;
}

/**
 * A typed time as 24-hour `HH:MM`, or null when it is not one yet. Accepts
 * `18:30`, `1830`, `6:30 pm`, `6:30p`, `630p`, `6pm` and `6` (06:00). Without
 * am/pm the hour is read on a 24-hour clock.
 */
export function parseTimeInput(text: string): string | null {
  const t = text.trim().toLowerCase().replace(/\s+/g, '').replace(/\./g, '');
  const m = /^(\d{1,2})(?::?(\d{2}))?(a|am|p|pm)?$/.exec(t);
  if (!m) return null;
  const minute = m[2] ? Number(m[2]) : 0;
  const hour = hour24(Number(m[1]), m[3]);
  if (hour === null || minute > 59) return null;
  return `${pad(hour)}:${pad(minute)}`;
}

/** "6:30 PM" for `18:30`; "" when there is no time. */
export function formatTimeLabel(value: string): string {
  const m = /^(\d{2}):(\d{2})$/.exec(value);
  if (!m) return '';
  const hour = Number(m[1]);
  const suffix = hour < 12 ? 'AM' : 'PM';
  return `${hour % 12 === 0 ? 12 : hour % 12}:${m[2]} ${suffix}`;
}

/** Every time of day on a `stepMinutes` grid, as 24-hour `HH:MM`. */
export function timeOptions(stepMinutes = 15): string[] {
  const out: string[] = [];
  for (let m = 0; m < 24 * 60; m += stepMinutes)
    out.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`);
  return out;
}
